import { ActionIcon, Box, Group } from '@mantine/core';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import type { DiffGroup } from '../../domain/entityDiff.js';
import { TypeIcon } from '../TypeGlyph/TypeIcon.js';
import { RecordLabel } from '../Diff/RecordLabel.js';
import { PushOpDetail } from './PushOpDetail.js';
import type { PushOpRow } from './PushOps.js';
import { ReviewEmpty } from './ReviewEmpty.js';
import { activeEntry, type RecordsView } from './useRecordsView.js';

/**
 * The record on screen: its header (name, key, and prev/next through the nav's own order) and
 * its diff — `PushOpDetail`, the same component a push result's expanded row already draws, so a
 * record reads the same before and after it is written. `rowActionFor` gives an op's differing
 * fields their **Adopt ▾** (the review's; a result has none).
 */
export const PushRecords = ({
    view,
    environment,
    rowActionFor,
}: {
    view: RecordsView;
    environment: string;
    rowActionFor?: (op: PushOpRow) => ((group: DiffGroup) => ReactNode) | undefined;
}) => {
    if (view.entries.length === 0) {
        return (
            <ReviewEmpty>
                Nothing to push to <span className="rgs-numeric">{environment}</span>.
            </ReviewEmpty>
        );
    }
    const entry = activeEntry(view);
    if (!entry) return <ReviewEmpty>No record matches. Clear the search or the field filter.</ReviewEmpty>;

    return (
        <Box>
            <Group
                justify="space-between"
                wrap="nowrap"
                px="md"
                py={8}
                style={{ borderBottom: '1px solid var(--rgs-line)' }}
            >
                <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                    <TypeIcon type={entry.op.entityType} size={16} stroke={1.7} style={{ flexShrink: 0 }} aria-hidden />
                    <RecordLabel title={entry.op.title} entityKey={entry.op.entityKey} layout="inline" size="sm" />
                </Group>
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label="Previous record"
                        disabled={!view.prev}
                        onClick={() => view.prev?.()}
                    >
                        <IconArrowLeft size={16} stroke={1.7} />
                    </ActionIcon>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label="Next record"
                        disabled={!view.next}
                        onClick={() => view.next?.()}
                    >
                        <IconArrowRight size={16} stroke={1.7} />
                    </ActionIcon>
                </Group>
            </Group>
            <Box p="md">
                <PushOpDetail op={entry.op} rowAction={rowActionFor?.(entry.op)} />
            </Box>
        </Box>
    );
};
